import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';
import { getSocket, disconnectSocket } from '../socket/socket';
import { sounds } from '../utils/sounds';

const BETS = [25, 50, 100, 250, 500, 1000, 2500, 5000, 10000, 25000];
const RED = { '♥': true, '♦': true };

/* ─── Confetti ─── */
function Confetti() {
  const pieces = Array.from({ length: 18 }, (_, i) => ({
    i,
    left: `${5 + i * 5.2}%`,
    color: ['#FBBF24','#34D399','#60A5FA','#F87171','#A78BFA','#FB923C'][i % 6],
    delay: `${(i * 0.07).toFixed(2)}s`,
    dur: `${0.7 + (i % 4) * 0.15}s`,
  }));
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {pieces.map(p => (
        <div
          key={p.i}
          className="absolute w-2.5 h-2.5 rounded-sm"
          style={{
            left: p.left, top: '-10px',
            background: p.color,
            animation: `confetti-fall ${p.dur} ${p.delay} ease-in forwards`,
          }}
        />
      ))}
    </div>
  );
}

/* ─── Card ─── */
function Card({ card, index = 0, animate = true }) {
  const [flipped, setFlipped] = useState(false);
  const wasHidden = useRef(card?.suit === '?');

  useEffect(() => {
    // When hidden card becomes visible → flip animation
    if (wasHidden.current && card && card.suit !== '?') {
      setFlipped(true);
      setTimeout(() => setFlipped(false), 400);
      sounds.dealerReveal();
    }
    wasHidden.current = card?.suit === '?';
  }, [card?.suit]);

  if (!card) return null;

  const isHidden = card.suit === '?';
  const isRed = RED[card.suit];
  const style = animate ? { animationDelay: `${index * 0.12}s` } : {};

  if (isHidden) {
    return (
      <div
        className="w-12 h-16 sm:w-14 sm:h-20 bg-gradient-to-br from-blue-700 to-blue-900 border-2 border-blue-500 rounded-lg shadow-lg flex items-center justify-center select-none card-deal"
        style={style}
      >
        <div className="w-8 h-12 sm:w-9 sm:h-14 border border-blue-400 rounded opacity-40" />
      </div>
    );
  }

  return (
    <div
      className={`w-12 h-16 sm:w-14 sm:h-20 bg-white border border-slate-200 rounded-lg shadow-lg flex flex-col justify-between p-1 select-none card-deal ${flipped ? 'card-flip' : ''}`}
      style={style}
    >
      <span className={`text-xs font-bold leading-none ${isRed ? 'text-red-500' : 'text-slate-900'}`}>{card.rank}</span>
      <span className={`text-base text-center leading-none ${isRed ? 'text-red-500' : 'text-slate-900'}`}>{card.suit}</span>
      <span className={`text-xs font-bold leading-none self-end rotate-180 ${isRed ? 'text-red-500' : 'text-slate-900'}`}>{card.rank}</span>
    </div>
  );
}

/* ─── Hand ─── */
function Hand({ cards = [], label, value, highlight, result }) {
  const isBust = value > 21;
  const isWin = result === 'win' || result === 'blackjack';

  return (
    <div className={`flex flex-col items-center gap-2 rounded-xl p-2 transition-all duration-300 ${
      highlight ? 'ring-2 ring-yellow-400 bg-green-800/40' : ''
    } ${isBust ? 'hand-bust' : ''} ${isWin ? 'hand-win' : ''}`}>
      <p className="text-xs text-slate-300 font-medium">{label}</p>
      <div className="flex gap-1.5">
        {cards.map((c, i) => <Card key={i} card={c} index={i} />)}
        {cards.length === 0 && (
          <div className="w-12 h-16 sm:w-14 sm:h-20 border-2 border-dashed border-green-700 rounded-lg opacity-30" />
        )}
      </div>
      {value !== undefined && cards.length > 0 && (
        <span className={`text-sm font-bold px-2 py-0.5 rounded ${
          isBust ? 'bg-red-500/20 text-red-400' :
          value === 21 ? 'bg-yellow-500/20 text-yellow-400' :
          'text-slate-300'
        }`}>
          {isBust ? 'BUST' : value === 21 ? '21 ✨' : value}
        </span>
      )}
    </div>
  );
}

/* ─── Result overlay ─── */
function ResultBadge({ result, payout, bet }) {
  const map = {
    blackjack: { label: 'BLACKJACK! 🃏', cls: 'bg-yellow-400 text-slate-900' },
    win:       { label: 'Nyertél! 🎉',   cls: 'bg-green-500 text-white' },
    push:      { label: 'Döntetlen 🤝',  cls: 'bg-slate-500 text-white' },
    loss:      { label: 'Veszítettél',   cls: 'bg-red-600 text-white' },
  };
  const m = map[result];
  if (!m) return null;
  const net = payout - bet;
  return (
    <div className="flex flex-col items-center gap-1 slide-up">
      <span className={`px-3 py-1 rounded-full text-xs font-bold ${m.cls}`}>{m.label}</span>
      {net !== 0 && (
        <span className={`text-sm font-bold ${net > 0 ? 'text-green-400' : 'text-red-400'}`}>
          {net > 0 ? '+' : ''}{Number(net).toLocaleString('hu')} 💰
        </span>
      )}
    </div>
  );
}

/* ─── Chip ─── */
function ChipButton({ value, selected, onClick }) {
  const colors = {
    25:    'from-gray-400 to-gray-500 border-gray-300',
    50:    'from-blue-500 to-blue-700 border-blue-300',
    100:   'from-red-500 to-red-700 border-red-300',
    250:   'from-green-500 to-green-700 border-green-300',
    500:   'from-purple-500 to-purple-700 border-purple-300',
    1000:  'from-yellow-400 to-yellow-600 border-yellow-200',
    2500:  'from-pink-500 to-pink-700 border-pink-300',
    5000:  'from-orange-500 to-orange-700 border-orange-300',
    10000: 'from-cyan-500 to-cyan-700 border-cyan-300',
    25000: 'from-rose-400 to-rose-600 border-rose-200',
  };
  return (
    <button
      onClick={() => { onClick(value); sounds.chip(); }}
      className={`relative w-14 h-14 rounded-full border-4 bg-gradient-to-br ${colors[value]} text-white font-bold text-xs shadow-lg transition-all duration-150 ${
        selected ? 'scale-110 ring-2 ring-white ring-offset-2 ring-offset-green-900' : 'hover:scale-105 opacity-80 hover:opacity-100'
      }`}
    >
      <span className="absolute inset-0 rounded-full border border-white/20" />
      {value >= 1000 ? `${value/1000}K` : value}
    </button>
  );
}

/* ─── Table Lobby ─── */
function TableLobby({ tables, onJoin, onCreate }) {
  return (
    <div className="max-w-2xl mx-auto">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-bold">Asztalok</h2>
        <button className="btn-gold" onClick={onCreate}>+ Új asztal</button>
      </div>
      {tables.length === 0 ? (
        <div className="card text-center text-slate-400 py-12">
          Nincs nyitott asztal. Hozz létre egyet!
        </div>
      ) : (
        <div className="space-y-3">
          {tables.map(t => (
            <div key={t.id} className="card flex items-center justify-between">
              <div>
                <p className="font-mono text-yellow-400 text-sm">#{t.id}</p>
                <p className="text-slate-400 text-sm mt-0.5">
                  {t.playerCount}/{t.maxPlayers} játékos –{' '}
                  <span className={t.state === 'waiting' ? 'text-green-400' : 'text-yellow-400'}>
                    {t.state === 'waiting' ? 'Csatlakozható' : 'Játék folyamatban'}
                  </span>
                </p>
              </div>
              {t.state === 'waiting' && t.playerCount < t.maxPlayers && (
                <button className="btn-gold text-sm" onClick={() => onJoin(t.id)}>Csatlakozás</button>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Game Table ─── */
function GameTable({ table, myId, onHit, onStand, onDouble, onLeave, onBet }) {
  const [bet, setBet] = useState(100);
  const [showConfetti, setShowConfetti] = useState(false);
  const prevState = useRef(table.state);

  const me = table.players.find(p => p.id === myId);
  const isMyTurn = table.state === 'playing' && table.players[table.currentPlayerIndex]?.id === myId;
  const canDouble = isMyTurn && me?.hand?.length === 2;

  const countdownSec = table.betCountdown
    ? Math.max(0, table.betCountdown - Math.floor(Date.now() / 1000))
    : null;

  // Sound & confetti on state change
  useEffect(() => {
    if (prevState.current !== table.state) {
      if (table.state === 'playing') {
        sounds.deal();
      }
      if (table.state === 'finished') {
        const myResult = table.players.find(p => p.id === myId)?.result;
        if (myResult === 'blackjack') { sounds.blackjack(); setShowConfetti(true); setTimeout(() => setShowConfetti(false), 2000); }
        else if (myResult === 'win')  { sounds.win(); setShowConfetti(true); setTimeout(() => setShowConfetti(false), 2000); }
        else if (myResult === 'loss') sounds.lose();
        else if (myResult === 'push') sounds.push();
      }
      prevState.current = table.state;
    }
  }, [table.state]);

  // Sound on hit/new card
  const prevCardCount = useRef(0);
  useEffect(() => {
    const myCards = me?.hand?.length || 0;
    if (myCards > prevCardCount.current && myCards > 2) sounds.hit();
    prevCardCount.current = myCards;
  }, [me?.hand?.length]);

  function handleBet(amount) {
    onBet(amount);
    // Chip fly animation
  }

  return (
    <div className="max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-bold">Asztal <span className="text-yellow-400 font-mono">#{table.id}</span></h2>
        <button className="btn-ghost text-sm" onClick={onLeave}>Kilépés</button>
      </div>

      {/* Green felt */}
      <div className="relative bg-gradient-to-b from-green-800 to-green-900 border-4 border-green-700 rounded-3xl p-3 sm:p-6 shadow-2xl overflow-hidden" style={{ minHeight: 300 }}>
        {/* Felt texture overlay */}
        <div className="absolute inset-0 opacity-5 pointer-events-none" style={{
          backgroundImage: 'repeating-linear-gradient(45deg, #000 0, #000 1px, transparent 0, transparent 50%)',
          backgroundSize: '8px 8px',
        }} />

        {showConfetti && <Confetti />}

        {/* Dealer */}
        <div className="flex justify-center mb-6">
          <div className="flex flex-col items-center gap-2">
            <p className="text-xs text-green-300 font-medium uppercase tracking-widest">Dealer</p>
            <div className="flex gap-1.5">
              {table.dealer.hand.map((c, i) => <Card key={i} card={c} index={i} />)}
              {table.dealer.hand.length === 0 && (
                <div className="w-12 h-16 sm:w-14 sm:h-20 border-2 border-dashed border-green-600 rounded-lg opacity-20" />
              )}
            </div>
            {table.dealer.hand.length > 0 && table.state !== 'waiting' && (
              <span className={`text-sm font-bold px-2 py-0.5 rounded ${
                table.dealer.handValue > 21 ? 'bg-red-500/20 text-red-400' :
                table.dealer.handValue === 21 ? 'bg-yellow-500/20 text-yellow-400' :
                'text-green-200'
              }`}>
                {table.dealer.handValue > 21 ? 'BUST' : table.dealer.handValue === 21 ? '21 ✨' : table.dealer.handValue}
              </span>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="border-t border-green-700/60 mb-6" />

        {/* Players */}
        <div className="flex flex-wrap justify-center gap-6">
          {table.players.map((p, i) => (
            <div key={p.id} className="flex flex-col items-center gap-2">
              <Hand
                cards={p.hand}
                label={p.id === myId ? `${p.username} (te)` : p.username}
                value={p.handValue}
                highlight={table.currentPlayerIndex === i && table.state === 'playing'}
                result={p.result}
              />
              <div className="flex flex-col items-center gap-1 text-xs">
                {p.bet > 0 && (
                  <span className="text-yellow-400 font-semibold">{Number(p.bet).toLocaleString('hu')} 💰</span>
                )}
                {p.result && <ResultBadge result={p.result} payout={p.payout} bet={p.bet} />}
                {p.status === 'bet_placed' && !p.result && (
                  <span className="text-green-400 text-xs">✓ Tét leadva</span>
                )}
                {p.status === 'waiting' && !p.bet && (
                  <span className="text-green-700 text-xs">Vár...</span>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Controls */}
      <div className="card mt-4">
        {/* Bet phase */}
        {table.state === 'waiting' && me?.bet === 0 && (
          <div>
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-slate-400">Válassz chipet és tedd fel a téted!</p>
              {countdownSec !== null && (
                <span className="text-yellow-400 font-bold tabular-nums">{countdownSec}s</span>
              )}
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 mb-5 scrollbar-hide justify-start sm:justify-center">
              {BETS.map(b => (
                <ChipButton key={b} value={b} selected={bet === b} onClick={setBet} />
              ))}
            </div>
            <button
              className="btn-gold w-full text-base py-3"
              onClick={() => { handleBet(bet); sounds.chip(); }}
            >
              Tét: {bet.toLocaleString('hu')} 💰
            </button>
          </div>
        )}

        {/* Waiting after bet */}
        {table.state === 'waiting' && me?.bet > 0 && (
          <div className="text-center py-2">
            <p className="text-green-400 text-sm font-medium">✓ Tét leadva – várakozás...</p>
            {countdownSec !== null && (
              <p className="text-slate-500 text-xs mt-1">Automatikus indítás: {countdownSec}s</p>
            )}
          </div>
        )}

        {/* Playing – my turn */}
        {table.state === 'playing' && isMyTurn && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <button
              className="btn-gold py-3 text-base"
              onClick={() => { onHit(); sounds.hit(); }}
            >
              🃏 Húz
            </button>
            <button
              className="btn-ghost py-3 text-base"
              onClick={() => { onStand(); sounds.stand(); }}
            >
              ✋ Megáll
            </button>
            {canDouble ? (
              <button
                className="col-span-2 sm:col-span-1 py-3 text-base btn bg-purple-600 hover:bg-purple-500 text-white"
                onClick={() => { onDouble(); sounds.chip(); }}
              >
                ⚡ Dupla
              </button>
            ) : null}
          </div>
        )}

        {/* Playing – other's turn */}
        {table.state === 'playing' && !isMyTurn && (
          <p className="text-center text-slate-400 text-sm py-2 animate-pulse">
            {table.players[table.currentPlayerIndex]?.username} húz...
          </p>
        )}

        {/* Dealer turn */}
        {table.state === 'dealer_turn' && (
          <p className="text-center text-yellow-400 text-sm py-2 animate-pulse">
            🎩 Dealer húz...
          </p>
        )}

        {/* Finished */}
        {table.state === 'finished' && (
          <p className="text-center text-slate-400 text-sm py-2">
            Következő kör 8 másodpercen belül...
          </p>
        )}
      </div>
    </div>
  );
}

/* ─── Main ─── */
export default function Blackjack() {
  const { user, updateBalance } = useAuth();
  const navigate = useNavigate();
  const [tables, setTables] = useState([]);
  const [table, setTable] = useState(null);
  const [error, setError] = useState('');
  const [socket, setSocket] = useState(null);

  useEffect(() => {
    const token = localStorage.getItem('token');
    const s = getSocket(token);
    s.connect();
    setSocket(s);

    s.on('tables_list', setTables);
    s.on('table_state', (t) => {
      setTable(t);
      if (t.state === 'finished') {
        setTimeout(() => {
          import('../api/client').then(({ default: api }) => {
            api.get('/wallet/balance').then(r => updateBalance(r.data.balance));
          });
        }, 1000);
      }
    });
    s.on('error', (msg) => { setError(msg); setTimeout(() => setError(''), 3000); });

    return () => {
      s.off('tables_list');
      s.off('table_state');
      s.off('error');
      disconnectSocket();
    };
  }, []);

  const onCreate  = useCallback(() => socket?.emit('create_table'), [socket]);
  const onJoin    = useCallback((id) => socket?.emit('join_table', id), [socket]);
  const onLeave   = useCallback(() => { socket?.emit('leave_table'); setTable(null); }, [socket]);
  const onBet     = useCallback((amount) => socket?.emit('place_bet', amount), [socket]);
  const onHit     = useCallback(() => socket?.emit('hit'), [socket]);
  const onStand   = useCallback(() => socket?.emit('stand'), [socket]);
  const onDouble  = useCallback(() => socket?.emit('double'), [socket]);

  return (
    <div className="min-h-screen bg-slate-900">
      <Navbar />
      <main className="max-w-5xl mx-auto px-4 py-10">
        <div className="flex items-center gap-3 mb-8">
          <button onClick={() => navigate('/')} className="text-slate-400 hover:text-slate-200 text-sm">← Lobby</button>
          <h1 className="text-2xl font-bold">🃏 Blackjack</h1>
        </div>

        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg px-4 py-2 text-sm mb-4 slide-up">
            {error}
          </div>
        )}

        {table ? (
          <GameTable
            table={table}
            myId={user.id}
            onHit={onHit}
            onStand={onStand}
            onDouble={onDouble}
            onLeave={onLeave}
            onBet={onBet}
          />
        ) : (
          <TableLobby tables={tables} onJoin={onJoin} onCreate={onCreate} />
        )}
      </main>
    </div>
  );
}
